import React from 'react';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  MenuItem,
  Stack,
  Chip,
  Typography,
} from '@mui/material';
import colors from '../../../assets/theme/colors';
import { getChildProfileCompletion } from '../../../utils/profileCompletion';
import { CHILD_TEAM_ACTION_COPY } from './childTeamActionConfig';
import { getDashboardActionDefinition } from './dashboardActionDefinitions';
import { TERMINOLOGY } from '../../../constants/terminology';

const SectionLabel = ({ children }) => (
  <Box sx={{ px: 1.5, pb: 0.75, pt: 1 }}>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.landing.textMuted }}>
      {children}
    </Typography>
  </Box>
);

const ActionItem = ({ icon, children, onClick, color = colors.landing.heroText, iconColor, ...props }) => (
  <MenuItem
    onClick={onClick}
    sx={{
      gap: 1.1,
      py: 1.15,
      px: 1.5,
      minHeight: 48,
      color,
      borderRadius: 0,
    }}
    {...props}
  >
    <ListItemIcon sx={{ minWidth: 34 }}>
      {icon ? React.cloneElement(icon, { sx: { fontSize: 18, color: iconColor || colors.brand.ink } }) : null}
    </ListItemIcon>
    {children}
  </MenuItem>
);

const ChildActionsMenuContent = ({
  child,
  onEditChild,
  onPrepForTherapy,
  onImportLogs,
  showPrepForTherapy = true,
  showImportLogs = true,
}) => {
  const canEdit = typeof onEditChild === 'function';
  const completionPercent = Math.max(0, Math.min(100, getChildProfileCompletion(child || {})));
  const profileLabel = child?.name || TERMINOLOGY.subject;
  const showSharingSection = showPrepForTherapy || showImportLogs;
  const shareAction = getDashboardActionDefinition('share');
  const importAction = getDashboardActionDefinition('import');
  const ShareIcon = shareAction?.icon;
  const ImportIcon = importAction?.icon;

  return (
    <>
      {showSharingSection ? (
        <>
          <SectionLabel>Sharing</SectionLabel>

          {showPrepForTherapy ? (
            <ActionItem
              icon={ShareIcon ? <ShareIcon /> : null}
              onClick={() => onPrepForTherapy?.(child)}
              iconColor={colors.brand.ink}
            >
              {shareAction?.menuLabel || CHILD_TEAM_ACTION_COPY.shareCareSummary}
            </ActionItem>
          ) : null}

          {showPrepForTherapy && showImportLogs ? <Divider sx={{ my: 0.5 }} /> : null}
        </>
      ) : null}

      <SectionLabel>Account</SectionLabel>

      {canEdit ? (
        <MenuItem
          onClick={() => onEditChild?.(child)}
          sx={{
            mx: 0.75,
            mb: 0.5,
            mt: 0.25,
            py: 1.1,
            px: 1.25,
            minHeight: 60,
            borderRadius: 2.5,
            border: `1px solid ${colors.landing.borderLight}`,
            bgcolor: colors.landing.surfaceSoft,
            color: colors.landing.heroText,
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Avatar
            src={child?.profilePhoto || child?.photoURL || child?.avatarUrl || undefined}
            alt={child?.name || profileLabel}
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.roles.careOwner.primary,
              color: colors.landing.surface,
              fontSize: '0.8rem',
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {child?.profilePhoto || child?.photoURL || child?.avatarUrl ? null : (child?.name?.[0] || profileLabel?.[0] || 'P')}
          </Avatar>

          <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0.15}>
            <Typography
              sx={{
                fontSize: '0.98rem',
                fontWeight: 800,
                lineHeight: 1.1,
                color: colors.landing.heroText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {profileLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.76rem',
                lineHeight: 1.15,
                color: colors.landing.textMuted,
              }}
            >
              {completionPercent > 0 ? `${completionPercent}% complete` : 'Profile setup'}
            </Typography>
          </Stack>

          <Chip
            label={CHILD_TEAM_ACTION_COPY.editChildInfo}
            size="small"
            sx={{
              height: 28,
              borderRadius: 999,
              px: 0.5,
              bgcolor: colors.landing.surface,
              border: `1px solid ${colors.landing.borderLight}`,
              color: colors.brand.ink,
              fontWeight: 800,
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </MenuItem>
      ) : null}

      {showImportLogs ? (
        <ActionItem
          icon={ImportIcon ? <ImportIcon /> : null}
          onClick={() => onImportLogs?.(child)}
          iconColor={colors.brand.deep}
        >
          {importAction?.menuLabel || CHILD_TEAM_ACTION_COPY.importPastData}
        </ActionItem>
      ) : null}
    </>
  );
};

export default ChildActionsMenuContent;
